import { WorkspaceAssistantPageContext, useWorkspaceAssistantSurface } from './WorkspaceAssistantContext';
import WorkspaceEvidence from './WorkspaceEvidence';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faExpand, faCompress, faCircleCheck, faCircleExclamation, faClock } from '@fortawesome/free-solid-svg-icons';

export function EmptyState({ title, children, action }) {
  return <div className="ow-empty"><span className="ow-empty-mark" aria-hidden="true">◌</span>
    <h3>{title}</h3><p>{children}</p>{action}</div>;
}

export function WorkspaceDialog({ title, subtitle, children, onClose, wide = false, expandable = false }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const fullscreen = expandable && expanded;
  const titleId = useId();
  const assistantPage = useContext(WorkspaceAssistantPageContext);
  useWorkspaceAssistantSurface(ref, { ...assistantPage, view: typeof title === 'string' ? title : 'Optimization detail', subtitle: typeof subtitle === 'string' ? subtitle : null });
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    const previous = document.activeElement;
    const workspace = dialog.closest('.ow-workspace');
    dialog.showModal();
    return () => {
      dialog.close();
      if (previous?.isConnected) previous.focus?.();
      else workspace?.querySelector('[aria-selected="true"], [aria-current="page"]')?.focus();
    };
  }, []);
  return <dialog ref={ref} className={`ow-dialog ${wide ? 'ow-dialog-wide' : ''} ${expandable ? 'ow-dialog-expandable' : ''} ${fullscreen ? 'ow-dialog-fullscreen' : ''}`} onCancel={event => {
    event.preventDefault(); onCloseRef.current();
  }} aria-labelledby={titleId}>
    <div className="ow-dialog-header"><div><h2 id={titleId}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
      <div className="ow-dialog-actions">
        {expandable && <button type="button" className="ow-icon-button" onClick={() => setExpanded(value => !value)}
          aria-label={fullscreen ? 'Restore dialog size' : 'Expand dialog to fullscreen'} aria-pressed={fullscreen}
          title={fullscreen ? 'Restore' : 'Expand'}><FontAwesomeIcon icon={fullscreen ? faCompress : faExpand} /></button>}
        <button type="button" className="ow-icon-button" onClick={onClose} aria-label="Close dialog"><FontAwesomeIcon icon={faXmark} /></button>
      </div></div>
    <div className="ow-dialog-body">{children}</div>
  </dialog>;
}

export function JudgeBadge({ judge, onClick }) {
  const verdict = judge?.verdict || 'pending';
  const labels = { pass: 'Judge passed', reject: 'Judge blocked', unknown: 'Needs evidence', error: 'Review unavailable', pending: 'Needs review' };
  const icon = verdict === 'pass' ? faCircleCheck : ['reject', 'error'].includes(verdict) ? faCircleExclamation : faClock;
  const content = <><FontAwesomeIcon icon={icon} />{labels[verdict] || 'Needs review'}</>;
  return onClick ? <button className={`ow-badge ow-judge-${verdict}`} onClick={onClick}>{content}</button>
    : <span className={`ow-badge ow-judge-${verdict}`}>{content}</span>;
}

export function JudgeDetails({ judge }) {
  if (!judge) return <EmptyState title="Independent review required">This action needs a passing judge review before it can proceed.</EmptyState>;
  return <div className="ow-judge-details"><JudgeBadge judge={judge} /><p>{judge.summary}</p>
    <dl className="ow-detail-grid"><div><dt>Stage</dt><dd>{judge.phase?.replaceAll('_', ' ')}</dd></div>
      <div><dt>Revision</dt><dd>{judge.revision}</dd></div></dl>
    <ul className="ow-checks">{judge.checks.map(check => <li key={check.id}>
      <span className={`ow-check-dot ow-check-${check.status}`} aria-label={check.status} />
      <div><strong>{check.id.replaceAll('_', ' ')}</strong><p>{check.reason}</p>
        <small>Evidence: {check.evidenceIds.join(', ')}</small></div></li>)}</ul>
    <WorkspaceEvidence judge={judge} />
  </div>;
}
